import React, { useCallback, useEffect, useState } from 'react';
import { InputTable } from '../../../../components/InputTable/InputTable.component';
import { ErrorList, InputTableColumns, inputTableBorder } from '../../../../components/InputTable/InputTable.type';
import { SessionsParametersLimitCell } from '../SessionsParametersCardLimit/SessionsParametersLimitCell/SessionsParametersLimitCell.component';
import {
  ExaminerBlockingLimit,
  ExaminerBlockingLimitPatch,
} from './ExaminerBlockingLimits.types';
import { getChangesFromArrayOfObject } from '../../../../utils/getChangesFromArrayOfObject';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { changeExaminerBlockingLimits } from '../../../../services/exams/examSheet/examSheet.service';
import { AxiosResponse } from 'axios';
import { OverflowTypograpy } from '../../../../components/OverflowTypography/OverflowTypography.component';
import { InactiveProcessBadge } from '../InactiveProcessBadge/InactiveProcessBadge.component';
import { AssessmentSessionProcessTypeId } from '../../../../services/exams/assessmentSession/assessments.types';
import { useTranslation } from 'react-i18next';

interface ExaminerBlockingLimitsProps {
  apiResponse: string;
  items: ExaminerBlockingLimit[];
  onClose: () => void;
  reload: () => void;
  isLoading: boolean;
  isExaminerView: boolean;
  onChange?: (isDataChanged: boolean) => void;
}

export const ExaminerBlockingLimits: React.FC<ExaminerBlockingLimitsProps> = ({
  apiResponse,
  items,
  onClose,
  reload,
  isLoading,
  isExaminerView,
  onChange,
}) => {
  const [parameters, setParameters] = useState<ExaminerBlockingLimit[]>(items);
  const [invalidType, setInvalidType] = useState<ErrorList[]>([]);
  const [edited, setEdited] = useState<boolean>(false);
  const { openAlert } = useLayoutContext();

  const { t } = useTranslation();

  const ExaminerBlockingLimitsColumns: InputTableColumns[] = [
    {
      id: 'examTaskGroupName',
      label: t('sessionsParameters:examinerBlockingLimits.table.examTaskGroupName'),
    },
    {
      id: 'totalLimitOutOfTolerance',
      label: t('sessionsParameters:examinerBlockingLimits.table.totalLimitOutOfTolerance'),
    },
    {
      id: 'sequentialLimitOutOfTolerance',
      label: t('sessionsParameters:examinerBlockingLimits.table.sequentialLimitOutOfTolerance'),
    },
    {
      id: 'totalLimitWithinTolerance',
      label: t('sessionsParameters:examinerBlockingLimits.table.totalLimitWithinTolerance'),
      style: { borderLeft: inputTableBorder, paddingLeft: 10 },
    },
    {
      id: 'sequentialLimitWithinTolerance',
      label: t('sessionsParameters:examinerBlockingLimits.table.sequentialLimitWithinTolerance'),
    },
  ];

  useEffect(() => {
    setParameters(items);
  }, [items]);

  const validate = (row: ExaminerBlockingLimit): ErrorList[] => {
    const {
      examTaskGroupId,
      totalLimitOutOfTolerance,
      sequentialLimitOutOfTolerance,
      totalLimitWithinTolerance,
      sequentialLimitWithinTolerance,
    } = row;

    const errorList: ErrorList[] = [];

    const sequentialBiggerThanTotalOutOfTolerance = totalLimitOutOfTolerance
      ? sequentialLimitOutOfTolerance && sequentialLimitOutOfTolerance < totalLimitOutOfTolerance
      : true;

    const sequantialBiggerThanTotalWithinTolerance = totalLimitWithinTolerance
      ? sequentialLimitWithinTolerance && sequentialLimitWithinTolerance < totalLimitWithinTolerance
      : true;

    if (
      sequentialLimitOutOfTolerance &&
      !(
        sequentialLimitOutOfTolerance >= 2 &&
        sequentialLimitOutOfTolerance <= 20 &&
        sequentialBiggerThanTotalOutOfTolerance
      )
    ) {
      errorList.push({
        type: 'sequentialLimitOutOfTolerance',
        id: examTaskGroupId,
        value: sequentialLimitOutOfTolerance,
      });
    }

    if (totalLimitOutOfTolerance && !(totalLimitOutOfTolerance >= 1 && totalLimitOutOfTolerance <= 20)) {
      errorList.push({
        type: 'totalLimitOutOfTolerance',
        id: examTaskGroupId,
        value: totalLimitOutOfTolerance,
      });
    }

    if (
      sequentialLimitWithinTolerance &&
      !(
        sequentialLimitWithinTolerance >= 2 &&
        sequentialLimitWithinTolerance <= 20 &&
        sequantialBiggerThanTotalWithinTolerance
      )
    ) {
      errorList.push({
        type: 'sequentialLimitWithinTolerance',
        id: examTaskGroupId,
        value: sequentialLimitWithinTolerance,
      });
    }

    if (totalLimitWithinTolerance && !(totalLimitWithinTolerance >= 1 && totalLimitWithinTolerance <= 20)) {
      errorList.push({
        type: 'totalLimitWithinTolerance',
        id: examTaskGroupId,
        value: totalLimitWithinTolerance,
      });
    }

    return errorList;
  };

  const valueValidation = (
    type: keyof ExaminerBlockingLimit,
    value: number | null,
    limits: ExaminerBlockingLimit,
  ): boolean => {
    const isValid = validate({ ...limits, [type]: value });
    if (!isValid.length) {
      setInvalidType((prev) => prev.filter((e) => e.id !== limits.examTaskGroupId));
      return isValid.length > 0;
    } else {
      setInvalidType((prev) => [...prev, ...isValid]);
      return isValid.length > 0;
    }
  };

  const handleValueCallback = useCallback(
    (key: keyof ExaminerBlockingLimit, value: number | null, id: string | number) => {
      setParameters((state: ExaminerBlockingLimit[]) => {
        return state.map((s: ExaminerBlockingLimit) => {
          if (s.examTaskGroupId === id) {
            if (value === 0) {
              s[key] = null as never;
            } else s[key] = value as never;
          }

          return s;
        });
      });
    },
    [parameters, setParameters],
  );

  const handleSwitchCallback = useCallback(
    (key: keyof ExaminerBlockingLimit, value: boolean, id: string | number) => {
      const defaultValue = key.includes('sequential') ? 2 : 1;
      setParameters((state) => {
        return state.map((s) => {
          if (s.examTaskGroupId === id) {
            s[key] = (value ? defaultValue : null) as never;
          }

          return s;
        });
      });
    },
    [parameters, setParameters],
  );

  useEffect(() => {
    const parametersPatch: ExaminerBlockingLimit[] = parameters.map((p) => {
      return {
        examTaskGroupId: p.examTaskGroupId,
        examTaskGroupName: p.examTaskGroupName,
        totalLimitOutOfTolerance: p.totalLimitOutOfTolerance ?? null,
        sequentialLimitOutOfTolerance: p.sequentialLimitOutOfTolerance ?? null,
        totalLimitWithinTolerance: p.totalLimitWithinTolerance ?? null,
        sequentialLimitWithinTolerance: p.sequentialLimitWithinTolerance ?? null,
        isProcessActive: p.isProcessActive,
      };
    });
    setEdited(JSON.stringify(parametersPatch) !== apiResponse);
  }, [parameters, apiResponse]);

  const handleDiscardCallback = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSaveCallback = useCallback(() => {
    const apiData: ExaminerBlockingLimit[] = JSON.parse(apiResponse);
    const filterData: ExaminerBlockingLimit[] = getChangesFromArrayOfObject<ExaminerBlockingLimit>(apiData, parameters);
    const data: ExaminerBlockingLimitPatch[] = filterData.map((p) => {
      return {
        examTaskGroupId: p.examTaskGroupId,
        totalLimitOutOfTolerance: p.totalLimitOutOfTolerance ?? null,
        sequentialLimitOutOfTolerance: p.sequentialLimitOutOfTolerance ?? null,
        totalLimitWithinTolerance: p.totalLimitWithinTolerance ?? null,
        sequentialLimitWithinTolerance: p.sequentialLimitWithinTolerance ?? null,
      };
    });
    changeExaminerBlockingLimits(data, isExaminerView)
      .then((res: AxiosResponse<boolean>) => {
        if (res.data) {
          openAlert('success');
          setEdited(false);
          reload();
        } else {
          openAlert('error');
        }
      })
      .catch(() => {
        openAlert('error');
      });
  }, [parameters, onClose]);

  const getAriaLabel = (type: string, examTaskGroupName: string) => {
    const limitOutOfToleranceLabel = `limit poza tolerancją dla grupy zadań ${examTaskGroupName}`;
    const limitWithinToleranceLabel = `limit w zakresie tolerancji dla grupy zadań ${examTaskGroupName}`;
    switch (type) {
      case 'totalLimitOutOfTolerance':
        return `Całkowity ${limitOutOfToleranceLabel}`;
      case 'sequentialLimitOutOfTolerance':
        return `Sekwencyjny ${limitOutOfToleranceLabel}`;
      case 'totalLimitWithinTolerance':
        return `Całkowity ${limitWithinToleranceLabel}`;
      case 'sequentialLimitWithinTolerance':
        return `Sekwencyjny ${limitWithinToleranceLabel}`;
    }
  };

  const getRowComponent = (
    type: string,
    value: ExaminerBlockingLimit,
    isValid: boolean,
  ): React.ReactNode | string | null => {
    if (value.isProcessActive) {
      switch (type) {
        case 'examTaskGroupName':
          return (
            <OverflowTypograpy
              style={{
                display: 'inline-block',
                fontWeight: 'bolder',
                fontSize: '1rem',
                color: '#3C419A',
                overflow: 'hidden',
                width: '160px',
                paddingTop: '5px',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {value[type]}
            </OverflowTypograpy>
          );
        case 'totalLimitOutOfTolerance':
        case 'sequentialLimitOutOfTolerance':
        case 'totalLimitWithinTolerance':
        case 'sequentialLimitWithinTolerance':
          return (
            <SessionsParametersLimitCell<ExaminerBlockingLimit>
              type={type}
              value={value}
              initialValue={value[type]}
              handleValueChange={handleValueCallback}
              handleSwitchChange={handleSwitchCallback}
              validation={valueValidation}
              isValid={isValid}
              idKey={'examTaskGroupId'}
              tripleSwitchMode={false}
              examTaskGroupName={value.examTaskGroupName}
              ariaLabel={getAriaLabel(type, value.examTaskGroupName)}
            />
          );
        default:
          return null;
      }
    } else {
      switch (type) {
        case 'examTaskGroupName':
          return (
            <OverflowTypograpy
              style={{
                display: 'inline-block',
                fontWeight: 'bolder',
                fontSize: '1rem',
                color: '#3C419A',
                width: '160px',
              }}
            >
              {value[type]}
            </OverflowTypograpy>
          );
        case 'totalLimitOutOfTolerance':
          return <InactiveProcessBadge processType={AssessmentSessionProcessTypeId.Control} />;
        default:
          return null;
      }
    }
  };

  const getValidationMessage = (error: ErrorList): string => {
    const isSequentialtype = error.type.includes('sequential');
    const minValue = isSequentialtype ? 2 : 1;
    const errorMessage = `Podana wartość jest nieprawidłowa, prawidłowa wartość powinna mieścić się w zakesie liczb ${minValue}-20`;
    const maxValue = 'Wartość limitu sekwencyjnego musi być mniejsza niż ustawiona wartość limitu całkowitego';
    if (isSequentialtype && error.value) {
      return error.value !== 1 && (error.value as number) <= 20 ? maxValue : errorMessage;
    } else {
      return errorMessage;
    }
  };

  useEffect(() => {
    onChange && onChange(edited);
  }, [edited]);

  const emptyMessage = 'Brak wyników';

  return (
    <InputTable<ExaminerBlockingLimit>
      columns={ExaminerBlockingLimitsColumns}
      parameters={parameters}
      items={items}
      keyParam={'examTaskGroupId'}
      invalidType={invalidType}
      headerText={t('sessionsParameters:examinerBlockingLimits.header')}
      onCancel={handleDiscardCallback}
      onSave={handleSaveCallback}
      getValidationMessage={getValidationMessage}
      getRowComponent={getRowComponent}
      edited={edited}
      emptyMessage={emptyMessage}
      activeProcessRow={'isProcessActive'}
      isLoading={isLoading}
    />
  );
};
